/**
 * TrustGrid Reports Page
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Button, Select, Alert } from 'antd';
import { DownloadOutlined, FileTextOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  generateTrustReport,
  generateFindingsReport,
  generateComplianceReport,
  generateCertificate,
  downloadBlob,
  getLatestScan
} from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { showError, showSuccess } from '../../../utils/notifications';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;

function Reports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3; // Fallback to 3 for backward compatibility

  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('trust');
  const [framework, setFramework] = useState('GDPR');
  const [latestScan, setLatestScan] = useState(null);
  const [error, setError] = useState(null);

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Reports', path: '/trustgrid/reports' }];
  }, [location.state]);

  useEffect(() => {
    loadLatestScan();
  }, []);

  const loadLatestScan = async () => {
    try {
      const response = await getLatestScan(projectId);
      setLatestScan(response.data);
    } catch (err) {
      const wasHandled = handleCrossOrgError(err, selectProject, navigate, 'Reports');
      if (!wasHandled) {
        console.error('Error loading latest scan:', err);
      }
    }
  };

  const handleGenerateReport = async (type) => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      let response;
      let filename;
      const timestamp = new Date().toISOString().split('T')[0];

      switch (type) {
        case 'trust':
          response = await generateTrustReport(projectId);
          filename = `trustgrid_report_${timestamp}.pdf`;
          break;
        case 'findings':
          response = await generateFindingsReport(projectId);
          filename = `findings_report_${timestamp}.pdf`;
          break;
        case 'compliance':
          response = await generateComplianceReport(projectId, framework);
          filename = `${framework.toLowerCase()}_compliance_${timestamp}.pdf`;
          break;
        default:
          throw new Error('Unknown report type');
      }

      // Download the PDF
      downloadBlob(response.data, filename);
      showSuccess(`Report generated successfully: ${filename}`);

    } catch (err) {
      console.error('Error generating report:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to generate report';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCertificate = async () => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await generateCertificate(projectId, framework);
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${framework.toLowerCase()}_certificate_${timestamp}.pdf`;

      downloadBlob(response.data, filename);
      showSuccess(`Certificate generated successfully: ${filename}`);

    } catch (err) {
      console.error('Error generating certificate:', err);
      let errorMsg = 'Failed to generate certificate';

      // Try to parse error from blob
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          // Ignore parsing errors
        }
      } else {
        errorMsg = err.response?.data?.error || err.message || errorMsg;
      }

      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadJSON = async () => {
    if (!latestScan) {
      showError('No scan data available');
      return;
    }

    try {
      const scanData = {
        scan_id: latestScan.id,
        trust_score: latestScan.trust_score,
        timestamp: latestScan.created_at,
        project_id: projectId,
        // Add more scan details as needed
      };

      const blob = new Blob([JSON.stringify(scanData, null, 2)], { type: 'application/json' });
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `trustgrid_data_${timestamp}.json`;

      downloadBlob(blob, filename);
      showSuccess(`JSON data exported: ${filename}`);
    } catch (err) {
      console.error('Error exporting JSON:', err);
      showError('Failed to export JSON data');
    }
  };

  return (
    <TrustGridPage
      title="Reports"
      subtitle="Generate compliance reports and certificates"
      breadcrumbs={breadcrumbs}
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: '24px' }}
        />
      )}

      <Card title="Trust Score Report" style={{ marginBottom: '16px' }}>
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>
            Comprehensive trust score analysis including:
          </p>
          <ul>
            <li>Overall trust score with color-coded assessment</li>
            <li>Control evaluation results (passing/failing)</li>
            <li>Executive summary and recommendations</li>
            <li>Evidence count per control</li>
          </ul>
          <div style={{ marginTop: '24px' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => handleGenerateReport('trust')}
              loading={generating}
            >
              Generate Trust Score Report
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Findings Report" style={{ marginBottom: '16px' }}>
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>
            Detailed security findings analysis including:
          </p>
          <ul>
            <li>Findings grouped by severity (Critical, High, Medium, Low)</li>
            <li>Severity distribution summary</li>
            <li>Detailed finding descriptions</li>
            <li>Associated control codes</li>
          </ul>
          <div style={{ marginTop: '24px' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => handleGenerateReport('findings')}
              loading={generating}
            >
              Generate Findings Report
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Compliance Report & Certificate" style={{ marginBottom: '16px' }}>
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>
            Framework-specific compliance reports and certificates:
          </p>
          <ul>
            <li>GDPR - General Data Protection Regulation</li>
            <li>SOC2 - Service Organization Control 2</li>
            <li>ISO27001 - Information Security Management</li>
            <li>EUAIACT - EU AI Act compliance</li>
          </ul>

          <div style={{ marginTop: '16px', marginBottom: '24px' }}>
            <label style={{ marginRight: '12px', fontWeight: 500 }}>Select Framework:</label>
            <Select
              value={framework}
              onChange={setFramework}
              style={{ width: 200 }}
            >
              <Option value="GDPR">GDPR</Option>
              <Option value="SOC2">SOC2</Option>
              <Option value="ISO27001">ISO 27001</Option>
              <Option value="EUAIACT">EU AI Act</Option>
            </Select>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => handleGenerateReport('compliance')}
              loading={generating}
            >
              {`Generate ${framework} Report`}
            </Button>
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              onClick={handleGenerateCertificate}
              loading={generating}
              style={{ backgroundColor: '#2e7d32', borderColor: '#2e7d32' }}
            >
              {`Generate ${framework} Certificate`}
            </Button>
          </div>

          <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
            Note: Certificates require minimum 70% compliance score
          </div>
        </div>
      </Card>

      <Card title="Export Data">
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '16px' }}>
            Export scan data in JSON format for custom analysis
          </p>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadJSON}
            disabled={!latestScan}
          >
            Download JSON
          </Button>
          {!latestScan && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Run a scan first to export data
            </div>
          )}
        </div>
      </Card>
    </TrustGridPage>
  );
}

export default Reports;
